// EditCourse.jsx
import React, { useState, useEffect, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  FiChevronLeft,
  FiChevronRight,
  FiX,
  FiMessageCircle,
  FiTrash2,
  FiMove,
  FiPlus,
} from "react-icons/fi";
import SharedLayout from "./SharedLayout";
import "./App.css";
import axios from "axios";
import DatePicker from "react-datepicker";
import { tr } from "date-fns/locale";
import InfoModal from "./modals/InfoModals";
import LinkModal from "./modals/LinkModal";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const EditCourse = () => {
  const { userId, courseId } = useParams();
  const navigate = useNavigate();

  // Info / Confirm modal state
  const [info, setInfo] = useState({
    open: false,
    type: "info", // info | success | error | warning
    title: "",
    message: "",
  });

  const [linkModalOpen, setLinkModalOpen] = useState(false);

  const openInfo = (type, title, message) =>
    setInfo({ open: true, type, title, message });
  const closeInfo = () => setInfo((p) => ({ ...p, open: false }));

  const [currentStep, setCurrentStep] = useState(1);
  const [currentDateTime, setCurrentDateTime] = useState({
    date: "",
    time: "",
    datetime: null,
  });

  // ---- Gün & soru şablonları
  const questionTemplate = () => ({
    id: `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
    text: "",
    options: ["", ""],
    correctOption: null,
  });

  const dayTemplate = () => ({
    lessonTitle: "",
    topicContent: "",
    assessment: {
      enabled: false,
      questions: [],
    },
    media: {
      documents: [],
      medias: [],
      links: [],
    },
  });

  const ALLOW_MULTI_QUESTIONS = false;

  // Step-2 gün verileri + aktif gün
  const [lessons, setLessons] = useState([dayTemplate()]);
  const [activeDay, setActiveDay] = useState(0);

  // Gün sekmeleri drag–drop için
  const [dragIndex, setDragIndex] = useState(null);

  // Step 1 & 3 genel form verileri
  const [formData, setFormData] = useState({
    courseName: "",
    description: "",
    coverImage: "",
    courseLength: "1",
    deliveryDate: "",
    repostEvery: "daily",
    deliveryTime: "",
    whatsappNotification: true,
  });

  // Şu anki tarih/saat
  useEffect(() => {
    const now = new Date();
    const date = now.toISOString().split("T")[0];
    const hours = String(now.getHours()).padStart(2, "0");
    const minutes = String(now.getMinutes()).padStart(2, "0");
    const time = `${hours}:${minutes}`;
    setCurrentDateTime({ date, time, datetime: now });
    setFormData((prev) => ({
      ...prev,
      deliveryDate: date,
      deliveryTime: time,
    }));
  }, []);

  // courseLength değişince lessons senkronize
  useEffect(() => {
    const len = Math.max(1, parseInt(formData.courseLength || "1", 10));
    setLessons((prev) => {
      let updated = [...prev];
      if (updated.length < len) {
        while (updated.length < len) updated.push(dayTemplate());
      } else if (updated.length > len) {
        updated = updated.slice(0, len);
      }
      return updated;
    });
    setActiveDay((d) => Math.min(d, len - 1));
  }, [formData.courseLength]);

  // ---- Step 3: tarih/saat kontrol & handler'lar
  const isDateTimeValid = (date, time) => {
    if (!date || !time) return false;
    const selected = new Date(`${date}T${time}`);
    const now = currentDateTime.datetime;
    return selected >= now;
  };

  const handleDeliveryDateChange = (e) => {
    const selectedDate = e.target.value;
    const now = currentDateTime.datetime;
    const currentDate = currentDateTime.date;

    const selectedDateObj = new Date(selectedDate);
    selectedDateObj.setHours(0, 0, 0, 0);

    const today = new Date(now);
    today.setHours(0, 0, 0, 0);

    if (selectedDateObj < today) return;

    setFormData((prev) => {
      let newTime = prev.deliveryTime;
      if (selectedDate === currentDate) {
        const currentTime = currentDateTime.time;
        if (prev.deliveryTime < currentTime) newTime = currentTime;
      }
      return { ...prev, deliveryDate: selectedDate, deliveryTime: newTime };
    });
  };

  const handleDeliveryTimeChange = (e) => {
    const selectedTime = e.target.value;
    const currentDate = currentDateTime.date;
    if (
      formData.deliveryDate === currentDate &&
      selectedTime < currentDateTime.time
    )
      return;
    setFormData((prev) => ({ ...prev, deliveryTime: selectedTime }));
  };

  // Step-3 gruplar
  const [groups, setGroups] = useState([]);
  const [selectedGroupId, setSelectedGroupId] = useState(0); // or null

  const fetchGroups = useCallback(async () => {
    try {
      const response = await axios.get(`${BACKEND_URL}/groups/${userId}`);

      const formattedGroups = response.data.map((group) => ({
        id: group.id,
        name: group.name,
        members: group.members.map((member) => ({
          id: member.id,
          number: member.number,
        })),
      }));

      setGroups(formattedGroups);
    } catch (err) {
      console.error("Error fetching groups:", err);
      openInfo("error", "Hata", "Gruplar yüklenirken bir sorun oluştu.");
    }
  }, [userId]); // eslint-disable-line

  useEffect(() => {
    fetchGroups();
  }, [fetchGroups]);

  // ---- Step 1 genel input/cover
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const MAX_COVER_MB = 2;

  const handleFileUpload = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const fileSizeMB = file.size / (1024 * 1024);
    if (fileSizeMB > MAX_COVER_MB) {
      openInfo(
        "warning",
        "Dosya çok büyük",
        `Kapak resmi en fazla ${MAX_COVER_MB} MB olabilir.`
      );
      return;
    }

    setFormData((prev) => ({ ...prev, coverImage: file }));
  };

  // ---- Step 2: Gün alanı helper'ları
  const updateLessonField = (idx, field, value) => {
    setLessons((prev) =>
      prev.map((d, i) => (i === idx ? { ...d, [field]: value } : d))
    );
  };

  const toggleAssessmentEnabled = (idx) => {
    setLessons((prev) =>
      prev.map((d, i) => {
        if (i !== idx) return d;
        const enabled = !d.assessment.enabled;
        const nextQuestions =
          enabled && d.assessment.questions.length === 0
            ? [questionTemplate()]
            : d.assessment.questions.slice(
                0,
                ALLOW_MULTI_QUESTIONS ? undefined : 1
              );
        return {
          ...d,
          assessment: {
            ...d.assessment,
            enabled,
            questions: nextQuestions,
          },
        };
      })
    );
  };

  const deleteQuestion = (dayIdx, qIdx) => {
    setLessons((prev) =>
      prev.map((d, i) => {
        if (i !== dayIdx) return d;
        const qs = d.assessment.questions.filter((_, j) => j !== qIdx);
        return { ...d, assessment: { ...d.assessment, questions: qs } };
      })
    );
  };

  const updateQuestionField = (dayIdx, qIdx, value) => {
    setLessons((prev) =>
      prev.map((d, i) => {
        if (i !== dayIdx) return d;
        const qs = d.assessment.questions.map((q, j) =>
          j === qIdx ? { ...q, text: value } : q
        );
        return { ...d, assessment: { ...d.assessment, questions: qs } };
      })
    );
  };

  const updateOption = (dayIdx, qIdx, optIdx, value) => {
    setLessons((prev) =>
      prev.map((d, i) => {
        if (i !== dayIdx) return d;
        const qs = d.assessment.questions.map((q, j) => {
          if (j !== qIdx) return q;
          const ops = [...q.options];
          ops[optIdx] = value;
          return { ...q, options: ops };
        });
        return { ...d, assessment: { ...d.assessment, questions: qs } };
      })
    );
  };

  const addOption = (dayIdx, qIdx) => {
    setLessons((prev) =>
      prev.map((d, i) => {
        if (i !== dayIdx) return d;
        const qs = d.assessment.questions.map((q, j) => {
          if (j !== qIdx) return q;
          if (q.options.length >= 5) return q;
          return { ...q, options: [...q.options, ""] };
        });
        return { ...d, assessment: { ...d.assessment, questions: qs } };
      })
    );
  };

  const removeOption = (dayIdx, qIdx, optIdx) => {
    setLessons((prev) =>
      prev.map((d, i) => {
        if (i !== dayIdx) return d;
        const qs = d.assessment.questions.map((q, j) => {
          if (j !== qIdx) return q;
          const ops = q.options.filter((_, k) => k !== optIdx);
          let correct = q.correctOption;
          if (correct === optIdx) correct = null;
          else if (correct > optIdx) correct = correct - 1;
          return { ...q, options: ops, correctOption: correct };
        });
        return { ...d, assessment: { ...d.assessment, questions: qs } };
      })
    );
  };

  const setCorrect = (dayIdx, qIdx, optIdx) => {
    setLessons((prev) =>
      prev.map((d, i) => {
        if (i !== dayIdx) return d;
        const qs = d.assessment.questions.map((q, j) =>
          j === qIdx ? { ...q, correctOption: optIdx } : q
        );
        return { ...d, assessment: { ...d.assessment, questions: qs } };
      })
    );
  };

  // Gün silme
  const deleteDay = (idx) => {
    if (lessons.length <= 1) return;
    setLessons((prev) => {
      const updated = prev.filter((_, i) => i !== idx);
      setActiveDay((a) => {
        if (a === idx) return Math.max(0, idx - 1);
        if (a > idx) return a - 1;
        return a;
      });
      setFormData((p) => ({ ...p, courseLength: String(updated.length) }));
      return updated;
    });
  };

  // Gün drag–drop
  const onDragStart = (idx) => setDragIndex(idx);
  const onDragEnd = () => setDragIndex(null);
  const onDrop = (dropIdx) => {
    if (dragIndex === null || dragIndex === dropIdx) return;
    setLessons((prev) => {
      const arr = [...prev];
      const [moved] = arr.splice(dragIndex, 1);
      arr.splice(dropIdx, 0, moved);
      setActiveDay((a) => {
        if (a === dragIndex) return dropIdx;
        if (dragIndex < dropIdx && a > dragIndex && a <= dropIdx) return a - 1;
        if (dragIndex > dropIdx && a >= dropIdx && a < dragIndex) return a + 1;
        return a;
      });
      return arr;
    });
    setDragIndex(null);
  };

  // Step geçişleri
  const nextStep = () => setCurrentStep((prev) => Math.min(prev + 1, 3));
  const prevStep = () => setCurrentStep((prev) => Math.max(prev - 1, 1));

  // Taslak veriyi getir
  const fetchCourseData = useCallback(async () => {
    if (!courseId) return;
    try {
      const response = await axios.get(`${BACKEND_URL}/courses/id/${courseId}`);

      if (!response.data) throw new Error("Sunucudan veri alınamadı.");

      // Parse the ISO date string directly to get date and time parts
      let formattedDate = "";
      let formattedTime = "";

      if (response.data.publishedAt) {
        // Extract date part (YYYY-MM-DD) and time part (HH:MM) directly from the ISO string
        const dateTimeParts = response.data.publishedAt.split("T");
        if (dateTimeParts.length === 2) {
          formattedDate = dateTimeParts[0]; // YYYY-MM-DD
          const timePart = dateTimeParts[1];
          formattedTime = timePart.substring(0, 5); // HH:MM
        }
      } else {
        // Fallback to current date/time if publishedAt is not available
        const now = new Date();
        formattedDate = now.toISOString().split("T")[0];
        formattedTime = now.toISOString().substring(11, 16);
      }

      setFormData((prev) => ({
        ...prev,
        courseName: response.data.name ?? "",
        description: response.data.description ?? "",
        coverImage: response.data.coverImage ?? "",
        publishedAt: response.data.publishedAt ?? "",
        courseLength: response.data.totalLessons?.toString() ?? "1",
        deliveryDate: formattedDate,
        deliveryTime: formattedTime,
      }));

      setLessons(
        response.data.lessons?.map((lesson) => ({
          lessonTitle: lesson.title ?? "",
          topicContent: lesson.content ?? "",
          day: lesson.day ?? 1,
          assessment: {
            enabled: !!lesson.quiz,
            questions: lesson.quiz
              ? [
                  {
                    id: questionTemplate().id,
                    text: lesson.quiz.question ?? "",
                    options: Array.isArray(lesson.quiz.options)
                      ? lesson.quiz.options
                      : [],
                    correctOption: lesson.quiz.correctOption || null,
                  },
                ]
              : [],
          },
          media: {
            documents: (Array.isArray(lesson.document)
              ? lesson.document
              : lesson.document
              ? [lesson.document]
              : []
            ).map((d) => (typeof d === "string" ? { url: d } : d)),

            medias: (Array.isArray(lesson.media)
              ? lesson.media
              : lesson.media
              ? [lesson.media]
              : []
            ).map((m) => (typeof m === "string" ? { url: m } : m)),

            links: (Array.isArray(lesson.externalLink)
              ? lesson.externalLink
              : lesson.externalLink
              ? [lesson.externalLink]
              : []
            ).map((l) => (typeof l === "string" ? { url: l } : l)),
          },
        })) || [dayTemplate()]
      );
    } catch (err) {
      console.error("Kurs verisi çekilemedi:", err);
      openInfo(
        "error",
        "Yükleme Hatası",
        err.response?.data?.message || "Kurs verisi yüklenemedi."
      );
    }
  }, [courseId]); // eslint-disable-line

  useEffect(() => {
    fetchCourseData();
    //console.log(lessons);
  }, [fetchCourseData]);

  // Form state
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleSubmit = async (mode = "PUBLISHED") => {
    // Validation — uyarılar InfoModal ile
    if (!isDateTimeValid(formData.deliveryDate, formData.deliveryTime))
      return openInfo(
        "warning",
        "Geçersiz Zaman",
        "Geçmiş gün/zaman seçemezsiniz!"
      );

    if (!selectedGroupId)
      return openInfo("warning", "Eksik Bilgi", "Lütfen bir grup seçiniz!");

    if (!formData.courseName.trim())
      return openInfo("warning", "Eksik Bilgi", "Lütfen kurs adını giriniz!");

    const hasEmptyLesson = lessons.some(
      (l) => !l.lessonTitle?.trim() || !l.topicContent?.trim()
    );
    if (hasEmptyLesson)
      return openInfo(
        "warning",
        "Eksik Ders",
        "Lütfen tüm dersler için başlık ve içerik giriniz!"
      );

    setIsSubmitting(true);

    for (let lesson of lessons) {
      if (lesson.assessment?.enabled && lesson.assessment.questions[0]){
        console.log(
          "question:",
          lesson.assessment.questions[0].text,
          "options:",
          lesson.assessment.questions[0].options,
          "correctOption:",
          lesson.assessment.questions[0].correctOption
        );
      }else{
        console.log("There is no assessment for this lesson:", lesson.lessonTitle);
      }
    }

    const requestBody = {
      courseId: Number(courseId),
      courseData: {
        name: formData.courseName.trim(),
        description: formData.description.trim(),
        coverImage: formData.coverImage,
        adminId: Number(userId),
        status: mode,
      },
      lessonsData: lessons.map((lesson, index) => ({
        title: lesson.lessonTitle,
        content: lesson.topicContent,
        day: index + 1,
        quiz:
          lesson.assessment.enabled && lesson.assessment.questions[0]
            ? {
                question: lesson.assessment.questions[0].text || "",
                options: lesson.assessment.questions[0].options || [],
                correctOption:
                  typeof lesson.assessment.questions[0].correctOption ===
                  "number"
                    ? lesson.assessment.questions[0].options[
                        lesson.assessment.questions[0].correctOption
                      ] || ""
                    : lesson.assessment.questions[0].correctOption || "",
              }
            : null,
      })),
      learnerIds:
        groups
          .find((group) => group.id === selectedGroupId)
          ?.members.map((member) => member.id) || [],
      scheduleTime: formData.deliveryTime,
      startDate: formData.deliveryDate,
      frequency: formData.repostEvery,
    };

    try {
      const { data } = await axios.put(
        `${BACKEND_URL}/courses/${courseId}`,
        requestBody,
        { headers: { "Content-Type": "application/json" } }
      );

      console.log(data);
      openInfo(
        "success",
        "Başarılı",
        mode === "PUBLISHED"
          ? "İçerik yayınlandı."
          : "Değişiklikler taslak olarak kaydedildi."
      );
      // istersen burada dashboard'a da döndürebilirsin:
      navigate(`/${userId}/dashboard`);
      resetForm();
    } catch (error) {
      console.error("İçerik kaydetme hatası:", error);
      const msg =
        error.response?.data?.message ||
        error.message ||
        "İçerik kaydedilirken bir hata oluştu.";
      openInfo("error", "Hata", msg);
    } finally {
      setIsSubmitting(false);
    }
  };

  const resetForm = () => {
    setCurrentStep(1);
    setFormData({
      courseName: "",
      description: "",
      coverImage: "",
      deliveryDate: "",
      deliveryTime: "",
      repostEvery: "daily",
    });
    setLessons([dayTemplate()]);
    setDragIndex(null);
    setActiveDay(0);
    setSelectedGroupId("");
  };

  const MAX_TOTAL = 30 * 1024 * 1024;

  const filenameFromUrl = (url) => {
    try {
      const u = new URL(url, window.location.origin);
      const name = u.pathname.split("/").pop() || "";
      return decodeURIComponent(name);
    } catch {
      const parts = (url || "").split("/");
      return decodeURIComponent(parts[parts.length - 1] || url);
    }
  };

  const uploadFile = async (file, type) => {
    // toplam boyut sınırını kontrol et
    if (totalSize + file.size > MAX_TOTAL) {
      openInfo(
        "warning",
        "Limit aşıldı",
        "Toplam dosya boyutu 30MB'ı geçemez!"
      );
      return null;
    }

    const form = new FormData();
    form.append("file", file);

    try {
      const { data } = await axios.post(`${BACKEND_URL}/upload/${type}`, form, {
        headers: { "Content-Type": "multipart/form-data" },
      });

      // boyut güncelle
      setTotalSize((prev) => prev + file.size);

      return { url: data.file.url, size: data.file.size };
    } catch (err) {
      console.error(`${type} upload error:`, err);
      openInfo("error", "Yükleme hatası", `${type} yüklenemedi.`);
      return null;
    }
  };

  const removeMediaItem = (dayIdx, key, idx) => {
    const fileObj = lessons[dayIdx].media[key][idx];
    const sizeToSubtract = fileObj.size || 0;
    const url = typeof fileObj === "string" ? fileObj : fileObj.url;
    const filename = url.split("/").pop();

    setLessons((prev) =>
      prev.map((d, i) =>
        i === dayIdx
          ? {
              ...d,
              media: {
                ...d.media,
                [key]: d.media[key].filter((_, j) => j !== idx),
              },
            }
          : d
      )
    );

    setTotalSize((prev) => Math.max(0, prev - sizeToSubtract));

    axios
      .delete(`${BACKEND_URL}/upload/${encodeURIComponent(filename)}`)
      .catch((err) => console.error("Sunucudan silme hatası:", err));
  };

  const [totalSize, setTotalSize] = useState(0);

  console.log(lessons);

  return (
    <SharedLayout pageTitle="İÇERİK DÜZENLEME">
      {/* Stepper */}
      <div className="mb-6 sm:mb-8">
        <div className="flex items-center justify-center overflow-x-auto no-scrollbar px-2">
          <div className="flex flex-wrap items-center justify-center gap-2 sm:gap-3">
            {[1, 2, 3].map((step) => (
              <React.Fragment key={step}>
                <button
                  onClick={() => currentStep >= step && setCurrentStep(step)}
                  className={`px-4 sm:px-6 py-2 rounded-full ${
                    currentStep === step
                      ? "green-button text-white"
                      : "bg-gray-200 text-gray-600"
                  } ${
                    currentStep < step ? "opacity-50 cursor-not-allowed" : ""
                  }`}
                  disabled={currentStep < step}
                >
                  {step === 1 && "GİRİŞ"}
                  {step === 2 && "İÇERİK"}
                  {step === 3 && "YAYINLAMA"}
                </button>
                {step < 3 && (
                  <FiChevronRight
                    className="text-gray-400 hidden sm:inline-block"
                    size={20}
                  />
                )}
              </React.Fragment>
            ))}
          </div>
        </div>
      </div>

      <div className="bg-gray-50 p-4 sm:p-8 rounded-2xl shadow-sm">
        {/* STEP 1 */}
        {currentStep === 1 && (
          <div className="form-step space-y-6">
            <div className="flex flex-col sm:flex-row sm:items-center pb-4 sm:pb-6 mb-4 sm:mb-8 gap-3">
              <label className="text-gray-700 text-sm font-medium sm:mr-2">
                İÇERİK SÜRESİ:
              </label>
              <div className="flex items-center">
                <select
                  name="courseLength"
                  value={formData.courseLength}
                  onChange={handleInputChange}
                  className="border border-gray-300 rounded-full sm:ml-3 px-4 sm:px-6 py-1 text-md"
                >
                  {Array.from({ length: 10 }, (_, i) => (
                    <option key={i + 1} value={i + 1}>
                      {i + 1}
                    </option>
                  ))}
                </select>
                <span className="text-gray-600 ml-3 text-md font-medium">
                  Tekrar
                </span>
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="block text-gray-700 text-sm font-bold mb-3 sm:mb-5">
                  İÇERİK ADI:
                </label>
                <div className="border-b border-gray-300 pb-1">
                  <input
                    type="text"
                    name="courseName"
                    value={formData.courseName}
                    onChange={handleInputChange}
                    maxLength={50}
                    className="w-full p-2 outline-none bg-transparent"
                    placeholder="İçerik adı yazınız"
                    required
                  />
                  <div className="text-xs text-gray-500 text-right">
                    {formData.courseName.length}/50
                  </div>
                </div>
              </div>

              <div>
                <label className="block text-gray-700 text-sm font-bold mb-3 sm:mb-5">
                  İÇERİK KAPAK RESMİ:
                </label>
                <div className="flex flex-col sm:flex-row items-start gap-4">
                  <div className="w-full sm:w-32 h-40 sm:h-32 bg-gray-100 border border-gray-300 rounded flex items-center justify-center overflow-hidden">
                    {formData.coverImage ? (
                      <img
                        src={
                          typeof formData.coverImage === "string"
                            ? formData.coverImage
                            : URL.createObjectURL(formData.coverImage)
                        }
                        alt="Preview"
                        className="w-full h-full object-cover"
                      />
                    ) : (
                      <span className="text-gray-400 text-xs">Önizleme</span>
                    )}
                  </div>

                  <div className="flex-1 min-w-0">
                    <p className="text-sm text-gray-600 mb-2">
                      Jpeg, gif veya png, maksimum 800x360px ve 2MB.
                    </p>
                    <label className="inline-block text-blue-600 hover:text-blue-800 cursor-pointer text-sm underline">
                      RESMİ YÜKLE
                      <input
                        type="file"
                        accept="image/jpeg,image/png,image/gif"
                        onChange={handleFileUpload}
                        className="hidden"
                      />
                    </label>
                  </div>
                </div>
              </div>
            </div>

            <div>
              <label className="block text-gray-700 text-sm font-bold mb-3 sm:mb-5">
                AÇIKLAMA:
              </label>
              <div className="relative">
                <textarea
                  name="description"
                  value={formData.description}
                  onChange={handleInputChange}
                  maxLength={200}
                  className="w-full p-3 border border-gray-300 rounded-lg bg-white"
                  rows={4}
                  placeholder="İçerik açıklamasını yazınız"
                  required
                />
                <div className="text-xs text-gray-500 text-right mt-1">
                  {formData.description.length}/200
                </div>
              </div>
            </div>

            <div className="pt-4 flex flex-col md:flex-row md:items-center md:justify-between gap-3">
              <button
                className="w-full md:w-auto px-6 py-2 red-button text-white rounded-full flex items-center justify-center"
                onClick={() => navigate(`/${userId}/dashboard`)}
              >
                <FiX className="mr-2" /> İPTAL
              </button>
              <div className="w-full md:w-auto flex flex-col sm:flex-row gap-3">
                <button
                  onClick={() => setCurrentStep(2)}
                  className="w-full sm:w-auto px-6 py-2 green-button text-white rounded-full flex items-center justify-center"
                >
                  İLERİ <FiChevronRight className="ml-2" />
                </button>
              </div>
            </div>
          </div>
        )}

        {/* STEP 2 */}
        {currentStep === 2 && (
          <div className="form-step">
            <div className="grid grid-cols-12 gap-4 sm:gap-6">
              {/* Sol: Gün sekmeleri */}
              <div className="col-span-12 md:col-span-3">
                <div className="p-3 bg-[#07183c] rounded-2xl">
                  <div className="text-white text-sm font-medium px-1 pb-2">
                    İçerik Günleri
                  </div>
                  <ul className="max-h-80 overflow-auto pr-1">
                    {lessons.map((_, i) => {
                      const isActive = activeDay === i;
                      return (
                        <li
                          key={i}
                          className="mb-2"
                          draggable
                          onDragStart={() => onDragStart(i)}
                          onDragOver={(e) => e.preventDefault()}
                          onDrop={() => onDrop(i)}
                          onDragEnd={onDragEnd}
                        >
                          <div
                            className={`flex items-center justify-between px-3 py-2 rounded-lg transition-colors
                              ${
                                isActive
                                  ? "bg-white/10 text-white font-extrabold"
                                  : "text-white/70 hover:text-white font-medium"
                              }`}
                          >
                            <button
                              className="flex-1 text-left"
                              onClick={() => setActiveDay(i)}
                            >
                              {i + 1}.Tekrar
                            </button>
                            <div className="flex items-center gap-2 ml-3">
                              <FiMove
                                className="opacity-70 cursor-grab"
                                title="Drag to reorder"
                              />
                              <button
                                type="button"
                                onClick={() => deleteDay(i)}
                                className={`opacity-70 hover:opacity-100 ${
                                  lessons.length <= 1
                                    ? "cursor-not-allowed"
                                    : ""
                                }`}
                                disabled={lessons.length <= 1}
                                title={
                                  lessons.length <= 1
                                    ? "En az 1 gün olması gerekiyor"
                                    : "Günü sil"
                                }
                              >
                                <FiTrash2 />
                              </button>
                            </div>
                          </div>
                        </li>
                      );
                    })}
                  </ul>
                </div>
              </div>

              {/* Sağ: Seçili güne ait içerik */}
              <div className="col-span-12 md:col-span-9 space-y-6">
                {/* Lesson Title */}
                <div>
                  <label className="block text-gray-700 text-sm font-medium mb-1">
                    KONU BAŞLIĞI
                  </label>
                  <div className="border-b border-gray-300 pb-1">
                    <input
                      type="text"
                      value={lessons[activeDay]?.lessonTitle || ""}
                      onChange={(e) =>
                        updateLessonField(
                          activeDay,
                          "lessonTitle",
                          e.target.value
                        )
                      }
                      maxLength={140}
                      className="w-full p-2 outline-none bg-transparent"
                      placeholder="Konu başlığını yazınız"
                      required
                    />
                    <div className="text-xs text-gray-500 text-right">
                      {(lessons[activeDay]?.lessonTitle || "").length}/140
                    </div>
                  </div>
                </div>

                {/* Topic Content */}
                <div>
                  <label className="block text-gray-700 text-sm font-medium mb-1">
                    KONU İÇERİK
                  </label>
                  <textarea
                    value={lessons[activeDay]?.topicContent || ""}
                    onChange={(e) =>
                      updateLessonField(
                        activeDay,
                        "topicContent",
                        e.target.value
                      )
                    }
                    className="w-full p-3 border border-gray-300 rounded-lg bg-white"
                    rows={4}
                    placeholder="Konunuzun içeriğini yazınız"
                    required
                  />
                </div>

                {/* Assessment */}
                <div>
                  <div className="flex items-center justify-between mb-3">
                    <label className="text-gray-700 text-sm font-medium">
                      DEĞERLENDİRME
                    </label>
                    <label className="relative inline-flex items-center cursor-pointer">
                      <input
                        type="checkbox"
                        className="sr-only peer"
                        checked={
                          lessons[activeDay]?.assessment.enabled || false
                        }
                        onChange={() => toggleAssessmentEnabled(activeDay)}
                      />
                      <div className="w-11 h-6 bg-gray-200 rounded-full peer peer-checked:after:translate-x-full after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-green-500"></div>
                    </label>
                  </div>

                  {lessons[activeDay]?.assessment.enabled && (
                    <div className="space-y-4">
                      {(lessons[activeDay]?.assessment.questions || [])
                        .slice(0, ALLOW_MULTI_QUESTIONS ? undefined : 1)
                        .map((q, qIdx) => (
                          <div
                            key={q.id}
                            className="border border-gray-200 bg-white rounded-xl shadow-sm p-4"
                          >
                            <div className="flex flex-col sm:flex-row sm:items-center justify-between mb-3 gap-2">
                              <div className="text-sm font-semibold text-gray-800">
                                SORU {qIdx + 1}
                              </div>
                              <button
                                type="button"
                                onClick={() => deleteQuestion(activeDay, qIdx)}
                                className="self-start sm:self-auto text-red-500 hover:text-red-600 flex items-center gap-2 text-sm"
                                title="Delete question"
                              >
                                <FiTrash2 /> KALDIR
                              </button>
                            </div>

                            <div className="mb-3">
                              <input
                                type="text"
                                value={q.text}
                                onChange={(e) =>
                                  updateQuestionField(
                                    activeDay,
                                    qIdx,
                                    e.target.value
                                  )
                                }
                                className="w-full p-2 border border-gray-300 rounded-lg bg-white"
                                placeholder="Sorunuzu yazınız..."
                                maxLength={140}
                              />
                              <div className="text-xs text-gray-500 text-right mt-1">
                                {q.text.length}/140
                              </div>
                            </div>

                            <div>
                              <label className="block text-gray-700 text-xs mb-2">
                                SEÇENEKLER
                              </label>
                              {q.options.map((opt, optIdx) => (
                                <div
                                  key={optIdx}
                                  className="flex flex-col sm:flex-row sm:items-center gap-2 mb-2"
                                >
                                  <div className="flex items-center">
                                    <input
                                      type="radio"
                                      name={`correct-${activeDay}-${qIdx}`}
                                      checked={
                                        q.correctOption === opt ||
                                        q.correctOption === optIdx
                                      } // First option is string(On render, fetched correctOption), second is number
                                      onChange={() =>
                                        setCorrect(activeDay, qIdx, optIdx)
                                      }
                                      className="mr-2"
                                    />
                                    <span className="text-xs text-gray-500 sm:hidden">
                                      Doğru
                                    </span>
                                  </div>
                                  <input
                                    type="text"
                                    value={opt}
                                    onChange={(e) =>
                                      updateOption(
                                        activeDay,
                                        qIdx,
                                        optIdx,
                                        e.target.value
                                      )
                                    }
                                    maxLength={100}
                                    className="flex-1 min-w-0 p-2 border border-gray-300 rounded-lg bg-white"
                                    placeholder={`Seçenek ${optIdx + 1}`}
                                  />
                                  {optIdx >= 2 && (
                                    <button
                                      type="button"
                                      onClick={() =>
                                        removeOption(activeDay, qIdx, optIdx)
                                      }
                                      className="sm:ml-2 text-red-500 hover:text-red-700 self-start sm:self-auto"
                                    >
                                      <FiX size={18} />
                                    </button>
                                  )}
                                </div>
                              ))}

                              {q.options.length < 5 && (
                                <button
                                  onClick={() => addOption(activeDay, qIdx)}
                                  className="mt-2 px-4 py-2 border border-green-500 text-green-500 rounded-full text-sm hover:bg-green-50 transition-colors flex items-center gap-2"
                                >
                                  <FiPlus /> SEÇENEK EKLE
                                </button>
                              )}
                            </div>
                          </div>
                        ))}
                    </div>
                  )}
                </div>

                {/* Media Resources (placeholder) */}
                {/* Media Resources */}
                <div>
                  <div className="flex flex-col sm:flex-row sm:items-center gap-2">
                    <label className="text-gray-700 text-sm font-medium">
                      MEDYA KAYNAKLARI
                    </label>
                    <div className="flex items-center space-x-3 text-sm">
                      {/* Document Upload */}
                      <label className="text-blue-600 hover:underline cursor-pointer">
                        DOKÜMAN YÜKLE
                        <input
                          type="file"
                          accept=".pdf,.doc,.docx"
                          className="hidden"
                          onChange={async (e) => {
                            const file = e.target.files?.[0];
                            e.target.value = "";
                            if (!file) return;
                            const uploaded = await uploadFile(file, "document");
                            if (!uploaded) return;
                            setLessons((prev) =>
                              prev.map((d, i) =>
                                i === activeDay
                                  ? {
                                      ...d,
                                      media: {
                                        ...d.media,
                                        documents: [
                                          ...d.media.documents,
                                          uploaded,
                                        ],
                                      },
                                    }
                                  : d
                              )
                            );
                          }}
                        />
                      </label>

                      {/* Media Upload */}
                      <label className="text-blue-600 hover:underline cursor-pointer">
                        MEDYA YÜKLE
                        <input
                          type="file"
                          accept="video/*,audio/*,image/*"
                          className="hidden"
                          onChange={async (e) => {
                            const file = e.target.files?.[0];
                            e.target.value = "";
                            if (!file) return;
                            const uploaded = await uploadFile(file, "media");
                            if (!uploaded) return;
                            setLessons((prev) =>
                              prev.map((d, i) =>
                                i === activeDay
                                  ? {
                                      ...d,
                                      media: {
                                        ...d.media,
                                        medias: [...d.media.medias, uploaded],
                                      },
                                    }
                                  : d
                              )
                            );
                          }}
                        />
                      </label>

                      {/* External Link */}
                      <button
                        type="button"
                        className="text-blue-600 hover:underline"
                        onClick={() => setLinkModalOpen(true)}
                      >
                        HARİCİ LİNK
                      </button>
                    </div>
                  </div>
                  {/* Listeler */}
                  <div className="grid md:grid-cols-3 gap-4 mt-4">
                    {/* Documents */}
                    <div className="bg-white border border-gray-200 rounded-lg p-3">
                      <div className="font-semibold text-sm mb-2">
                        Dokümanlar
                      </div>
                      <ul className="space-y-2">
                        {(lessons[activeDay]?.media?.documents || []).length ===
                        0 ? (
                          <li className="text-xs text-gray-500">
                            Henüz doküman yok.
                          </li>
                        ) : (
                          lessons[activeDay].media.documents.map((u, idx) => (
                            <li
                              key={idx}
                              className="flex items-center justify-between text-sm"
                            >
                              <a
                                href={u.url}
                                target="_blank"
                                rel="noreferrer"
                                className="truncate text-blue-600 hover:underline"
                                title={u.url}
                              >
                                {filenameFromUrl(u.url)}
                              </a>
                              <button
                                type="button"
                                className="ml-2 text-red-500 hover:text-red-600"
                                onClick={() =>
                                  removeMediaItem(activeDay, "documents", idx)
                                }
                                title="Kaldır"
                              >
                                <FiTrash2 />
                              </button>
                            </li>
                          ))
                        )}
                      </ul>
                    </div>

                    {/* Medias */}
                    <div className="bg-white border border-gray-200 rounded-lg p-3">
                      <div className="font-semibold text-sm mb-2">Medyalar</div>
                      <ul className="space-y-2">
                        {(lessons[activeDay]?.media?.medias || []).length ===
                        0 ? (
                          <li className="text-xs text-gray-500">
                            Henüz medya yok.
                          </li>
                        ) : (
                          lessons[activeDay].media.medias.map((u, idx) => (
                            <li
                              key={idx}
                              className="flex items-center justify-between text-sm"
                            >
                              <a
                                href={u.url}
                                target="_blank"
                                rel="noreferrer"
                                className="truncate text-blue-600 hover:underline"
                                title={u.url}
                              >
                                {filenameFromUrl(u.url)}
                              </a>
                              <button
                                type="button"
                                className="ml-2 text-red-500 hover:text-red-600"
                                onClick={() =>
                                  removeMediaItem(activeDay, "medias", idx)
                                }
                                title="Kaldır"
                              >
                                <FiTrash2 />
                              </button>
                            </li>
                          ))
                        )}
                      </ul>
                    </div>

                    {/* Links */}
                    <div className="bg-white border border-gray-200 rounded-lg p-3">
                      <div className="font-semibold text-sm mb-2">Linkler</div>
                      <ul className="space-y-2">
                        {(lessons[activeDay]?.media?.links || []).length ===
                        0 ? (
                          <li className="text-xs text-gray-500">
                            Henüz link yok.
                          </li>
                        ) : (
                          lessons[activeDay].media.links.map((u, idx) => {
                            const urlValue = typeof u === "string" ? u : u.url;
                            return (
                            <li
                              key={idx}
                              className="flex items-center justify-between text-sm"
                            >
                              <a
                                href={urlValue}
                                target="_blank"
                                rel="noreferrer"
                                className="truncate text-blue-600 hover:underline"
                                title={urlValue}
                              >
                                {urlValue}
                              </a>
                              <button
                                type="button"
                                className="ml-2 text-red-500 hover:text-red-600"
                                onClick={() =>
                                  removeMediaItem(activeDay, "links", idx)
                                }
                                title="Kaldır"
                              >
                                <FiTrash2 />
                              </button>
                            </li>
                            );
                          })
                        )}
                      </ul>
                    </div>
                  </div>

                  <div className="mt-3 text-xs text-gray-600">
                    Yüklenen: {(totalSize / (1024 * 1024)).toFixed(2)} MB / 30
                    MB
                  </div>
                  <div className="w-full bg-gray-200 h-2 rounded">
                    <div
                      className="bg-green-500 h-2 rounded"
                      style={{
                        width: `${Math.min(
                          (totalSize / MAX_TOTAL) * 100,
                          100
                        )}%`,
                      }}
                    />
                  </div>
                </div>

                {/* Buttons */}
                <div className="pt-6 flex flex-col md:flex-row md:items-center md:justify-between gap-3">
                  <button
                    className="w-full md:w-auto px-6 py-2 red-button text-white rounded-full flex items-center justify-center"
                    onClick={() => navigate(`/${userId}/dashboard`)}
                  >
                    <FiX className="mr-2" /> İPTAL
                  </button>

                  <div className="w-full md:w-auto flex flex-col sm:flex-row gap-3">
                    <button
                      onClick={prevStep}
                      className="w-full sm:w-auto px-6 py-2 border border-green-500 text-green-500 bg-transparent rounded-full flex items-center justify-center hover:bg-green-50"
                    >
                      <FiChevronLeft className="mr-2" /> GERİ
                    </button>
                    <button
                      onClick={() => {
                        handleSubmit("DRAFT");
                      }}
                      disabled={isSubmitting}
                      className={`w-full sm:w-auto px-6 py-2 ${
                        isSubmitting ? "bg-gray-400" : "bg-[#07183c]"
                      } text-white rounded-full flex items-center justify-center min-w-24`}
                    >
                      {isSubmitting ? (
                        <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white mr-2"></div>
                      ) : null}
                      {isSubmitting ? "KAYDEDİLİYOR..." : "KAYDET"}
                    </button>
                    <button
                      onClick={nextStep}
                      className="w-full sm:w-auto px-6 py-2 green-button text-white rounded-full flex items-center justify-center"
                    >
                      İLERİ <FiChevronRight className="ml-2" />
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* STEP 3 */}
        {currentStep === 3 && (
          <div className="form-step">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
              <div>
                <h3 className="text-lg font-bold text-gray-800 mb-2">
                  YAYINLAMA SEÇENEKLERİ
                </h3>
                <p className="text-gray-600 text-sm">
                  Kursunuzun ilk dersini vermek için en uygun zamanı seçin
                  (diğer tüm dersler her gün aynı saatte verilecektir).
                </p>
              </div>

              <div className="flex justify-start mt-2 mb-2">
                <select
                  className="border border-gray-300 rounded-full px-4 py-2 bg-white text-gray-700 w-full max-w-md"
                  value={selectedGroupId}
                  onChange={(e) => setSelectedGroupId(Number(e.target.value))}
                >
                  <option value="">KATILIMCI LİSTESİ</option>
                  {groups.map((group) => (
                    <option key={group.id} value={group.id}>
                      {group.name}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            <div className="space-y-4 mb-6">
              <div className="flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-3">
                <label className="text-gray-700 text-sm font-medium w-full sm:w-40">
                  YAYINLAMA TARİHİ:
                </label>
                <DatePicker
                  selected={
                    formData.deliveryDate
                      ? new Date(formData.deliveryDate)
                      : null
                  }
                  onChange={(date) =>
                    setFormData((prev) => ({
                      ...prev,
                      deliveryDate: date
                        ? date.toLocaleDateString("en-CA")
                        : "",
                      deliveryTime:
                        date &&
                        date.toDateString() === new Date().toDateString() &&
                        prev.deliveryTime <
                          new Date().toLocaleTimeString("tr-TR", {
                            hour: "2-digit",
                            minute: "2-digit",
                            hour12: false,
                          })
                          ? new Date().toLocaleTimeString("tr-TR", {
                              hour: "2-digit",
                              minute: "2-digit",
                              hour12: false,
                            })
                          : prev.deliveryTime,
                    }))
                  }
                  dateFormat="dd-MM-yyyy"
                  locale={tr}
                  placeholderText="01-01-2000"
                  className="border border-gray-300 rounded-full px-4 py-2 bg-white w-full sm:w-auto"
                  minDate={new Date()}
                  isClearable
                />
              </div>

              <div className="flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-3">
                <label className="text-gray-700 text-sm font-medium w-full sm:w-40">
                  TEKRARLAMA SIKLIĞI:
                </label>
                <select
                  name="repostEvery"
                  value={formData.repostEvery}
                  onChange={handleInputChange}
                  className="border border-gray-300 rounded-full px-4 py-2 bg-white w-full sm:w-32"
                >
                  <option value="daily">Gün</option>
                  <option value="weekly">Hafta</option>
                  <option value="monthly">Ay</option>
                </select>
              </div>

              <div className="flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-3">
                <label className="text-gray-700 text-sm font-medium w-full sm:w-40">
                  TEKRARLAMA ZAMANI:
                </label>
                <DatePicker
                  selected={
                    formData.deliveryTime
                      ? new Date(`1970-01-01T${formData.deliveryTime}`)
                      : null
                  }
                  onChange={(date) =>
                    setFormData((prev) => ({
                      ...prev,
                      deliveryTime: date
                        ? date.toLocaleTimeString("tr-TR", {
                            hour: "2-digit",
                            minute: "2-digit",
                            hour12: false,
                          })
                        : "",
                    }))
                  }
                  showTimeSelect
                  showTimeSelectOnly
                  timeIntervals={5}
                  timeCaption="Saat"
                  dateFormat="HH:mm"
                  timeFormat="HH:mm"
                  placeholderText="00:00"
                  value={formData.deliveryTime}
                  className="border border-gray-300 rounded-full px-4 py-2 bg-white w-full sm:w-auto"
                  isClearable
                  minTime={
                    formData.deliveryDate ===
                    new Date().toISOString().split("T")[0]
                      ? new Date()
                      : new Date(0, 0, 0, 0, 0)
                  }
                  maxTime={new Date(0, 0, 0, 23, 59)}
                />
              </div>
            </div>

            <div className="bg-white p-3 rounded-full inline-flex items-center mb-8 border border-gray-200">
              <input
                type="checkbox"
                id="whatsapp-checkbox"
                className="h-5 w-5 rounded border-gray-300 text-green-500 focus:ring-green-500"
                checked={formData.whatsappNotification}
                onChange={() =>
                  setFormData((prev) => ({
                    ...prev,
                    whatsappNotification: !prev.whatsappNotification,
                  }))
                }
              />
              <label
                htmlFor="whatsapp-checkbox"
                className="ml-2 flex items-center"
              >
                <FiMessageCircle className="text-green-500" size={20} />
              </label>
            </div>

            <div className="pt-4 border-t border-gray-200 flex flex-col md:flex-row md:items-center md:justify-between gap-3">
              <button
                className="w-full md:w-auto px-6 py-2 red-button text-white rounded-full flex items-center justify-center"
                onClick={() => navigate(`/${userId}/dashboard`)}
              >
                <FiX className="mr-2" /> İPTAL
              </button>
              <div className="w-full md:w-auto flex flex-col sm:flex-row gap-3">
                <button
                  onClick={prevStep}
                  className="w-full sm:w-auto px-6 py-2 border border-green-500 text-green-500 bg-transparent rounded-full flex items-center justify-center"
                >
                  <FiChevronLeft className="mr-2" /> GERİ
                </button>
                <button
                  onClick={() => {
                    handleSubmit("DRAFT");
                  }}
                  disabled={isSubmitting}
                  className={`w-full sm:w-auto px-6 py-2 ${
                    isSubmitting ? "bg-gray-400" : "bg-[#07183c]"
                  } text-white rounded-full flex items-center justify-center min-w-24`}
                >
                  {isSubmitting ? (
                    <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white mr-2"></div>
                  ) : null}
                  {isSubmitting ? "KAYDEDİLİYOR..." : "KAYDET"}
                </button>

                <button
                  onClick={() => handleSubmit("PUBLISHED")}
                  disabled={isSubmitting}
                  className={`w-full sm:w-auto px-6 py-2 ${
                    isSubmitting ? "bg-gray-400" : "green-button"
                  } text-white rounded-full flex items-center justify-center min-w-24`}
                >
                  {isSubmitting ? (
                    <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white mr-2"></div>
                  ) : (
                    <>
                      YAYINLA <FiChevronRight className="ml-2" />
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Modals */}
      <LinkModal
        open={linkModalOpen}
        onClose={() => setLinkModalOpen(false)}
        onSave={(url) => {
          setLessons((prev) =>
            prev.map((d, i) =>
              i === activeDay
                ? {
                    ...d,
                    media: {
                      ...d.media,
                      links: [...(d.media.links || []), url],
                    },
                  }
                : d
            )
          );
        }}
      />

      <InfoModal
        open={info.open}
        type={info.type}
        title={info.title}
        message={info.message}
        onClose={closeInfo}
      />
    </SharedLayout>
  );
};

export default EditCourse;
